import { legacy, QCloseButton, QStack } from '@qualio/ui-components';
import { useTranslation } from 'react-i18next';

import {
  PeriodicReviewExtended,
  PeriodicReviewUser,
} from '../../../../../api/model/periodicReview';
import { PeriodicReviewDetails } from './types';

import { usePeriodicReview } from './hooks';

import { useFormatDate } from '../../../../../hooks/useFormatDate';
import { CenteredSpinner } from './CenteredSpinner';
import { DetailCardLayout } from './DetailCardLayout';
import { ReviewDetails } from './ReviewDetails';

const buildStatus = (
  t: (key: string) => string,
  periodicReview?: PeriodicReviewExtended,
) => {
  if (!periodicReview) {
    return '';
  }

  const notApproved = periodicReview.round_stopped
    ? t('statuses.approvalDeclined')
    : t('labels.inProgress');

  return periodicReview.conclusion ? t('statuses.approved') : notApproved;
};

const buildReviewPeriod = (periodicReview?: PeriodicReviewExtended) => {
  if (!periodicReview) {
    return 0;
  }

  const reviewDiscussionAfterRoundStarted = periodicReview.discussion.filter(
    (item) => {
      return item.timestamp >= periodicReview.review_round_started_at;
    },
  )[0];

  return reviewDiscussionAfterRoundStarted?.suggested_review_period || 0;
};

const getApproverDetails = (
  approver: PeriodicReviewUser,
  periodicReview: PeriodicReviewExtended,
) => {
  const discussionItem = periodicReview.discussion.find(
    (item) => item.user.id === approver.id && item.hasOwnProperty('approved'),
  );

  if (!discussionItem) {
    return {
      user: approver,
    };
  }

  const { timestamp, approved, comment } = discussionItem;

  return {
    timestamp,
    approved,
    comment,
    user: approver,
  };
};

const getPeriodicReviewDetails = (
  t: (key: string) => string,
  formatDate: (utcDate: Date | string, includeTime?: boolean) => string,
  periodicReview?: PeriodicReviewExtended,
) => {
  if (!periodicReview) {
    return {};
  }

  const reviewPeriod = buildReviewPeriod(periodicReview);
  const period = reviewPeriod || t('labels.none');
  const periodLabel = reviewPeriod ? t('labels.month') : '';
  const pluralSuffix = reviewPeriod > 1 ? 's' : '';
  const reviewPeriodStr = `${period} ${periodLabel}${pluralSuffix}`;
  const reviewApprovals = periodicReview.approvers.map((approver) =>
    getApproverDetails(approver, periodicReview),
  );

  return {
    status: buildStatus(t, periodicReview),
    sent_by: periodicReview.author.full_name,
    sent_at:
      periodicReview.review_started_at &&
      formatDate(new Date(periodicReview.review_round_started_at * 1000)),
    comment: periodicReview.discussion[0]?.comment || t('labels.none'),
    review_period: reviewPeriodStr,
    approvals: reviewApprovals,
  };
};

export type PeriodicReviewDetailsPanelProps = legacy.QDrawerProps & {
  periodicReviewId?: number;
};

export const PeriodicReviewDetailsPanel = ({
  isOpen,
  onClose,
  periodicReviewId,
}: PeriodicReviewDetailsPanelProps) => {
  const { t } = useTranslation();
  const {
    data: periodicReview,
    isLoading,
    isError,
  } = usePeriodicReview(periodicReviewId);
  const formatDate = useFormatDate();

  return (
    <legacy.QDrawer isOpen={isOpen} onClose={onClose} size="lg">
      <legacy.QDrawerHeader>
        {t('labels.periodicReview')}
        <QCloseButton onClick={onClose} />
      </legacy.QDrawerHeader>
      <QStack>
        {isLoading && <CenteredSpinner />}
        {!isLoading && !isError && (
          <QStack spacing="6">
            <DetailCardLayout title={t('labels.reviewDetails')}>
              <ReviewDetails
                details={
                  getPeriodicReviewDetails(
                    t,
                    formatDate,
                    periodicReview,
                  ) as PeriodicReviewDetails
                }
              />
            </DetailCardLayout>
          </QStack>
        )}
      </QStack>
    </legacy.QDrawer>
  );
};
